(() => {
  const updateSummary = (root) => {
    const summary = root.querySelector('[data-yn-selection-summary]');
    if (!summary) return;

    const values = [];
    root.querySelectorAll('variant-selects fieldset').forEach((fieldset) => {
      const selected = fieldset.querySelector('input[type="radio"]:checked');
      if (selected) values.push(selected.value);
    });

    root.querySelectorAll('variant-selects select').forEach((select) => {
      if (select.value) values.push(select.options[select.selectedIndex]?.text?.trim() || select.value);
    });

    const length = root.querySelector('[data-product-length] input:checked');
    const lengthHeading = root.querySelector('[data-product-length] [data-selected-value]');
    const lengthOutput = summary.querySelector('[data-yn-length-value]');
    if (lengthHeading) lengthHeading.textContent = length?.value || '';
    if (lengthOutput) {
      lengthOutput.textContent = length?.value || lengthOutput.dataset.placeholder;
      lengthOutput.closest('.yn-pdp-selection__length')?.toggleAttribute('hidden', !root.querySelector('[data-product-length]'));
    }

    const variantOutput = summary.querySelector('[data-yn-variant-values]');
    if (variantOutput) variantOutput.textContent = [...new Set(values)].join(' · ');
  };

  const setAccordionState = (item, isOpen) => {
    const trigger = item.querySelector('.yn-pdp-accordion__trigger');
    const panel = item.querySelector('.yn-pdp-accordion__panel');
    if (!trigger || !panel) return;

    panel.ynAccordionAnimation?.cancel();
    panel.ynAccordionAnimation = null;
    trigger.setAttribute('aria-expanded', String(isOpen));
    item.classList.toggle('is-open', isOpen);
    panel.hidden = !isOpen;
    panel.style.height = isOpen ? 'auto' : '0px';
  };

  const animateAccordion = (item, isOpen) => {
    const trigger = item.querySelector('.yn-pdp-accordion__trigger');
    const panel = item.querySelector('.yn-pdp-accordion__panel');
    if (!trigger || !panel) return;

    const startHeight = panel.hidden ? 0 : panel.getBoundingClientRect().height;
    panel.ynAccordionAnimation?.cancel();

    trigger.setAttribute('aria-expanded', String(isOpen));
    item.classList.toggle('is-open', isOpen);
    panel.hidden = false;

    const endHeight = isOpen ? panel.scrollHeight : 0;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || startHeight === endHeight) {
      setAccordionState(item, isOpen);
      return;
    }

    panel.style.height = `${startHeight}px`;
    const animation = panel.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      { duration: 300, easing: 'ease-in-out' }
    );
    panel.ynAccordionAnimation = animation;

    animation.finished
      .then(() => {
        if (panel.ynAccordionAnimation !== animation) return;
        panel.ynAccordionAnimation = null;
        panel.hidden = !isOpen;
        panel.style.height = isOpen ? 'auto' : '0px';
      })
      .catch(() => {});
  };

  const initializeAccordions = (root = document) => {
    const scopes = root.matches?.('product-info') ? [root] : root.querySelectorAll('product-info');
    scopes.forEach((scope) => {
      if (scope.dataset.ynAccordionsInitialized) return;
      scope.dataset.ynAccordionsInitialized = 'true';
      let hasOpenItem = false;
      scope.querySelectorAll('.yn-pdp-accordion').forEach((item) => {
        const trigger = item.querySelector('.yn-pdp-accordion__trigger');
        const shouldOpen = !hasOpenItem && trigger?.getAttribute('aria-expanded') === 'true';
        setAccordionState(item, shouldOpen);
        hasOpenItem = hasOpenItem || shouldOpen;
      });
      scope.addEventListener('click', (event) => {
        const trigger = event.target.closest('.yn-pdp-accordion__trigger');
        if (!trigger || !scope.contains(trigger)) return;

        const item = trigger.closest('.yn-pdp-accordion');
        const openItem = scope.querySelector('.yn-pdp-accordion.is-open');

        if (openItem === item) {
          animateAccordion(item, false);
          return;
        }

        if (openItem) animateAccordion(openItem, false);
        animateAccordion(item, true);
      });
    });
  };

  const positionBundleOffer = (productInfo) => {
    const submitButton = productInfo.querySelector('.product-form__submit');
    const purchaseMeta = productInfo.querySelector('.yn-pdp-atc-meta');
    const bundleOffer = productInfo.querySelector('.bundler-volume-target-element');
    const anchor = purchaseMeta || submitButton;
    if (!submitButton || !bundleOffer) return false;

    if (anchor.nextElementSibling !== bundleOffer) {
      anchor.insertAdjacentElement('afterend', bundleOffer);
    }

    const reasons = [...productInfo.querySelectorAll('[data-yn-bundle-reasons] > span')]
      .map((reason) => reason.textContent.trim())
      .filter(Boolean);
    bundleOffer.querySelectorAll('.bndlr-radio-container').forEach((option, index) => {
      if (!reasons[index]) return;
      let reason = option.querySelector('.yn-pdp-bundle-reason');
      if (!reason) {
        reason = document.createElement('span');
        reason.className = 'yn-pdp-bundle-reason';
        option.append(reason);
      }
      reason.textContent = reasons[index];
    });

    bundleOffer.dataset.ynBundlePositioned = 'true';
    return true;
  };

  const initializeBundleOffer = (productInfo) => {
    if (positionBundleOffer(productInfo) || productInfo.dataset.ynBundleObserverInitialized) return;

    productInfo.dataset.ynBundleObserverInitialized = 'true';
    const observer = new MutationObserver(() => {
      if (!positionBundleOffer(productInfo)) return;
      observer.disconnect();
      delete productInfo.dataset.ynBundleObserverInitialized;
    });

    observer.observe(productInfo, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 10000);
  };

  const initializeRequiredOptions = (productInfo) => {
    const lengthRoot = productInfo.querySelector('[data-product-length]');
    const form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
    const submitButton = form?.querySelector('button[name="add"]');
    const submitLabel = submitButton?.querySelector('span');
    if (!lengthRoot || !form || !submitButton || !submitLabel || form.dataset.ynRequiredOptionsInitialized) return;

    form.dataset.ynRequiredOptionsInitialized = 'true';
    submitButton.dataset.ynAddLabel = submitLabel.textContent.trim();

    const isLengthMissing = () => !lengthRoot.querySelector('input[type="radio"]:checked');

    const focusLengthSelector = () => {
      lengthRoot.classList.add('yn-pdp-option--needs-attention');
      lengthRoot.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => lengthRoot.querySelector('input[type="radio"]')?.focus({ preventScroll: true }), 350);
    };

    const syncSubmitState = () => {
      const missingLength = isLengthMissing();
      const unavailable = submitButton.disabled;

      submitButton.classList.toggle('yn-pdp-submit--prompt', missingLength && !unavailable);
      submitButton.toggleAttribute('data-yn-option-prompt', missingLength && !unavailable);

      if (missingLength && !unavailable) {
        const prompt = lengthRoot.dataset.chooseLabel || 'Choose a length';
        submitLabel.textContent = prompt;
        submitButton.setAttribute('aria-label', prompt);
      } else if (!missingLength && !unavailable) {
        submitLabel.textContent = submitButton.dataset.ynAddLabel;
        submitButton.removeAttribute('aria-label');
        lengthRoot.classList.remove('yn-pdp-option--needs-attention');
      }
    };

    form.addEventListener('submit', (event) => {
      if (!isLengthMissing()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      focusLengthSelector();
    }, true);

    submitButton.addEventListener('click', (event) => {
      if (!isLengthMissing()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      focusLengthSelector();
    }, true);

    lengthRoot.addEventListener('change', () => {
      lengthRoot.classList.remove('yn-pdp-option--needs-attention');
      window.setTimeout(syncSubmitState, 40);
    });

    document.addEventListener('variant-change', () => window.setTimeout(syncSubmitState, 40));
    syncSubmitState();
  };

  const addBusinessDays = (date, numberOfDays) => {
    const result = new Date(date);
    let added = 0;

    while (added < numberOfDays) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }

    return result;
  };

  const formatDeliveryDate = (date) =>
    new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' }).format(date);

  const checkDeliveryEstimate = (estimator) => {
    const input = estimator.querySelector('[data-yn-delivery-input]');
    const result = estimator.querySelector('[data-yn-delivery-result]');
    if (!input || !result) return;

    const pincode = input.value.replace(/\D/g, '').slice(0, 6);
    input.value = pincode;

    if (!/^[1-9][0-9]{5}$/.test(pincode)) {
      input.setAttribute('aria-invalid', 'true');
      result.classList.add('is-error');
      result.textContent = estimator.dataset.error;
      return;
    }

    const earliest = addBusinessDays(new Date(), 5);
    const latest = addBusinessDays(new Date(), 7);
    const dates = `${formatDeliveryDate(earliest)}–${formatDeliveryDate(latest)}`;

    input.removeAttribute('aria-invalid');
    result.classList.remove('is-error');
    result.innerHTML = `<strong>${estimator.dataset.result
      .replace('__PIN__', pincode)
      .replace('__DATES__', dates)}</strong><small>${estimator.dataset.note}</small>`;
  };

  const sizeChart = [
    { name: 'XS', widths: [14, 10, 11, 10, 8] },
    { name: 'S', widths: [15, 11, 12, 11, 8] },
    { name: 'M', widths: [16, 12, 13, 12, 9] },
    { name: 'L', widths: [16, 13, 14, 13, 10] },
  ];

  const findSizeInput = (productInfo, sizeName) => {
    const aliases = {
      XS: ['xs', 'extra small'],
      S: ['s', 'small'],
      M: ['m', 'medium'],
      L: ['l', 'large'],
    };
    const acceptedValues = aliases[sizeName] || [sizeName.toLowerCase()];

    return [...(productInfo?.querySelectorAll('[data-yn-size-options] input[type="radio"]') || [])].find((input) =>
      acceptedValues.includes(input.value.trim().toLowerCase())
    );
  };

  // Dawn's scroll-reveal wrapper around the product info column
  // (.scroll-trigger.animate--slide-in, present when "Show dynamic
  // sections on scroll" is on) keeps a non-"none" CSS transform on
  // itself even after its entrance animation finishes. Any transformed
  // ancestor becomes the containing block for descendant
  // `position: fixed` elements, which is exactly what a native
  // <dialog>'s built-in centering relies on — so left nested inside
  // that wrapper, the size-finder dialog centers against that
  // ancestor's box instead of the viewport and ends up looking shifted
  // to one side. Moving it to a direct child of <body> (never
  // transformed) sidesteps the problem. We cache the product-info
  // ancestor on the dialog first, since closest('product-info') would
  // otherwise stop working once the dialog is detached from it.
  // The read-only size-chart popup (data-yn-chart-dialog) shares the same
  // nested-dialog problem even though it doesn't need the product-info
  // lookups the measurement dialog does, so it's reparented here too.
  const reparentSizeDialogs = (root) => {
    root.querySelectorAll('[data-yn-size-dialog], [data-yn-chart-dialog]').forEach((dialog) => {
      if (!dialog._ynProductInfo) {
        dialog._ynProductInfo = dialog.closest('product-info');
      }
      if (dialog.parentElement !== document.body) {
        document.body.appendChild(dialog);
      }
    });
  };

  const getProductInfo = (dialog) => dialog?._ynProductInfo || dialog?.closest('product-info') || null;

  const recommendSize = (dialog) => {
    const fields = [...dialog.querySelectorAll('[data-yn-size-measurement]')];
    const result = dialog.querySelector('[data-yn-size-result]');
    if (!result) return;

    const measurements = fields.map((field) => Number.parseFloat(field.value));
    if (measurements.some((measurement) => !Number.isFinite(measurement))) {
      result.classList.add('is-error');
      result.textContent = dialog.dataset.incomplete;
      fields.find((field) => !field.value)?.focus();
      return;
    }

    const recommendation = sizeChart.find((size) =>
      measurements.every((measurement, index) => measurement <= size.widths[index])
    );

    result.classList.remove('is-error');
    if (!recommendation) {
      result.innerHTML = `<strong>${dialog.dataset.custom}</strong><a href="/pages/custom-orders">Start a custom order</a>`;
      return;
    }

    const productInfo = getProductInfo(dialog);
    const sizeInput = findSizeInput(productInfo, recommendation.name);
    const message = dialog.dataset.result.replace('__SIZE__', recommendation.name);

    if (!sizeInput || sizeInput.classList.contains('disabled')) {
      result.innerHTML = `<strong>${message}</strong><span>${dialog.dataset.unavailable.replace('__SIZE__', recommendation.name)}</span>`;
      return;
    }

    result.innerHTML = `<strong>${message}</strong><button type="button" data-yn-size-apply data-size="${recommendation.name}">${dialog.dataset.apply.replace('__SIZE__', recommendation.name)}</button>`;
  };

  document.addEventListener('click', (event) => {
    const deliveryButton = event.target.closest('[data-yn-delivery-submit]');
    if (deliveryButton) {
      checkDeliveryEstimate(deliveryButton.closest('[data-yn-delivery-estimator]'));
      return;
    }

    const sizeOpen = event.target.closest('[data-yn-size-open]');
    if (sizeOpen) {
      const dialog = document.getElementById(sizeOpen.getAttribute('aria-controls'));
      dialog?.showModal();
      dialog?.querySelector('[data-yn-size-measurement]')?.focus();
      return;
    }

    const sizeClose = event.target.closest('[data-yn-size-close]');
    if (sizeClose) {
      sizeClose.closest('dialog')?.close();
      return;
    }

    const chartOpen = event.target.closest('[data-yn-chart-open]');
    if (chartOpen) {
      document.getElementById(chartOpen.getAttribute('aria-controls'))?.showModal();
      return;
    }

    const chartClose = event.target.closest('[data-yn-chart-close]');
    if (chartClose) {
      chartClose.closest('dialog')?.close();
      return;
    }

    const sizeCalculate = event.target.closest('[data-yn-size-calculate]');
    if (sizeCalculate) {
      recommendSize(sizeCalculate.closest('[data-yn-size-dialog]'));
      return;
    }

    const sizeApply = event.target.closest('[data-yn-size-apply]');
    if (sizeApply) {
      const dialog = sizeApply.closest('[data-yn-size-dialog]');
      const productInfo = getProductInfo(dialog);
      const sizeInput = findSizeInput(productInfo, sizeApply.dataset.size);
      sizeInput?.click();
      dialog?.close();
    }
  });

  document.addEventListener('yn-size-scanned', (event) => {
    // Fired by <yn-camera-size-finder> once it has a hand reading. Fill the
    // same manual inputs the typed-measurement flow uses, then run the
    // existing recommendSize() logic so both paths share one result path.
    const dialog = event.target.closest('[data-yn-size-dialog]');
    if (!dialog) return;

    const measurements = event.detail?.measurements || {};
    dialog.querySelectorAll('[data-yn-size-measurement]').forEach((field) => {
      const finger = field.dataset.ynSizeMeasurement;
      if (measurements[finger] != null) field.value = measurements[finger];
    });

    recommendSize(dialog);
    dialog.querySelector('[data-yn-size-result]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches('[data-yn-delivery-input]')) return;
    event.preventDefault();
    checkDeliveryEstimate(event.target.closest('[data-yn-delivery-estimator]'));
  });

  const initialize = (root = document) => {
    root.querySelectorAll('product-info').forEach((productInfo) => {
      updateSummary(productInfo);
      initializeBundleOffer(productInfo);
      initializeRequiredOptions(productInfo);
    });
    initializeAccordions(root);
    reparentSizeDialogs(root);
  };

  document.addEventListener('change', (event) => {
    const productInfo = event.target.closest('product-info');
    if (productInfo) updateSummary(productInfo);
  });

  document.addEventListener('product-info:loaded', (event) => {
    updateSummary(event.target);
    initializeBundleOffer(event.target);
    initializeRequiredOptions(event.target);
    initializeAccordions(event.target);
    reparentSizeDialogs(event.target);
  });
  document.addEventListener('DOMContentLoaded', () => initialize());
})();
